import CtaButtonGroup, { CtaType } from "@/components/molecules/CtaButtonGroup";
import Text from "./shared/Text";
import {
  faStar,
  faStarHalfStroke,
} from "@fortawesome/pro-solid-svg-icons";
import { faStar as faStarRegular } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

/**
 * AlternativeHeroSection — hero for competitor-comparison pages.
 *
 * Layout (desktop two-column, mobile stacked):
 *   LEFT  — eyebrow (competitor chip + text) · two-line split title ·
 *           primary CTA · "Trusted by" label · 3 review-site badges.
 *   RIGHT — white testimonial card (title, quote, avatar, name, role).
 *   BELOW — 3 stats cards in a single row.
 *
 * A peach/pink gradient wash matching ProductHeroV2 fills the lower
 * band of the section.
 */

export type AlternativeHeroReviewBadge = {
  name: string;
  iconSrc?: string;
  rating: number;
};

export type AlternativeHeroTestimonial = {
  title: string;
  quote: string;
  name: string;
  role: string;
  avatar?: string;
};

export type AlternativeHeroStat = {
  icon?: string;
  iconDef?: IconDefinition;
  value: string;
  label: string;
  accentColor?: string;
};

export type AlternativeHeroData = {
  eyebrow: string;
  eyebrowIcon?: string;
  eyebrowInitial?: string; // single uppercase letter used for the colored chip
  eyebrowChipBg?: string; // tailwind bg class for the chip
  eyebrowImage?: string; // competitor symbol image — rendered instead of the letter when provided
  eyebrowColorClass?: string;
  titleMuted: string;
  titleBright: string;
  subtitle?: string;
  primaryCta: CtaType;
  ctas?: CtaType[];
  trustedByLabel?: string;
  trustedByColorClass?: string;
  reviewBadges: AlternativeHeroReviewBadge[];
  testimonial: AlternativeHeroTestimonial;
  stats: AlternativeHeroStat[];
};

const Stars = ({ rating }: { rating: number }) => {
  const full = Math.floor(rating);
  const hasHalf = rating - full >= 0.25 && rating - full < 0.75;
  const fullCount = rating - full >= 0.75 ? full + 1 : full;
  return (
    <div className="flex items-center gap-1" aria-label={`${rating} out of 5`}>
      {Array.from({ length: 5 }).map((_, i) => {
        if (i < fullCount) {
          return (
            <FontAwesomeIcon
              key={i}
              icon={faStar}
              className="h-3.5 w-3.5 text-[#F97316]"
              aria-hidden="true"
            />
          );
        }
        if (i === fullCount && hasHalf) {
          return (
            <FontAwesomeIcon
              key={i}
              icon={faStarHalfStroke}
              className="h-3.5 w-3.5 text-[#F97316]"
              aria-hidden="true"
            />
          );
        }
        return (
          <FontAwesomeIcon
            key={i}
            icon={faStarRegular}
            className="h-3.5 w-3.5 text-neutralBlack/20"
            aria-hidden="true"
          />
        );
      })}
    </div>
  );
};

const AlternativeHeroSection = (props: AlternativeHeroData) => {
  const {
    eyebrow,
    eyebrowInitial,
    eyebrowImage,
    eyebrowChipBg = "bg-[#F97316]",
    eyebrowColorClass = "text-[#F97316]",
    titleMuted,
    titleBright,
    subtitle,
    primaryCta,
    ctas,
    trustedByLabel = "Trusted by 200,000+ creators",
    trustedByColorClass = "text-darkSlate04",
    reviewBadges,
    testimonial,
    stats,
  } = props;

  return (
    <section className="relative w-full overflow-hidden bg-white">
      {/* Peach/pink gradient wash — softly blends into the section below. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-[75%]"
        style={{
          background:
            "linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 228, 214, 0.3) 50%, rgba(255, 228, 214, 0.18) 100%)," +
            "radial-gradient(ellipse 60% 90% at 0% 100%, rgba(255, 180, 210, 0.36) 0%, rgba(255, 180, 210, 0.06) 60%, rgba(255, 255, 255, 0) 95%)," +
            "radial-gradient(ellipse 60% 90% at 100% 100%, rgba(255, 210, 160, 0.3) 0%, rgba(255, 210, 160, 0.06) 60%, rgba(255, 255, 255, 0) 95%)",
          maskImage:
            "linear-gradient(to bottom, black 0%, black 75%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(to bottom, black 0%, black 75%, transparent 100%)",
        }}
      />

      <div className="container relative z-10 mx-auto px-4 pb-12 pt-10 sm:px-6 md:px-8 md:pb-16 md:pt-16 lg:px-12 lg:pb-20 lg:pt-20">
        <div className="mx-auto max-w-6xl">
        {/* Top two-column row */}
        <div className="grid grid-cols-1 items-stretch gap-10 lg:grid-cols-2 lg:gap-14">
          {/* LEFT column */}
          <div>
            <div className="flex items-center justify-center gap-2 lg:justify-start">
              {eyebrowInitial ? (
                <span
                  className={`inline-flex h-6 w-6 items-center justify-center overflow-hidden rounded-full text-[12px] font-bold text-white ${eyebrowChipBg}`}
                  aria-hidden="true"
                >
                  {eyebrowImage ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={eyebrowImage} alt="" className="h-full w-full object-contain" />
                  ) : (
                    eyebrowInitial
                  )}
                </span>
              ) : null}
              <Text
                variant="eyebrow"
                className={eyebrowColorClass}
                style={{ letterSpacing: "0.6px" }}
              >
                {eyebrow}
              </Text>
            </div>

            <Text
              as="h1"
              variant="display-lg"
              className="mt-5 text-center text-[24px] sm:text-[24px] md:text-[40px] lg:text-left lg:text-[48px]"
            >
              <span className="text-neutralBlack">{titleMuted}</span>
              <br />
              <span className="text-neutralBlack">{titleBright}</span>
            </Text>

            {subtitle ? (
              <Text
                as="p"
                variant="body-md"
                className="mt-4 text-[14px] leading-relaxed text-darkSlate04 sm:text-[15px]"
              >
                {subtitle}
              </Text>
            ) : null}

            <div className="mt-8 flex flex-col items-center gap-5 sm:flex-row sm:items-center lg:items-start">
              <CtaButtonGroup
                ctas={ctas ?? [primaryCta]}
                gap="flex gap-3"
                fullWidthOnMobile={false}
              />
            </div>

            {trustedByLabel ? (
              <Text
                as="p"
                variant="body-md"
                className={`mt-8 text-center text-[13px] font-medium lg:text-left ${trustedByColorClass}`}
              >
                {trustedByLabel}
              </Text>
            ) : null}

            {reviewBadges && reviewBadges.length ? (
              <div className="mt-4 flex flex-wrap items-center justify-center gap-6 lg:justify-start">
                {reviewBadges.map((b) => (
                  <div
                    key={b.name}
                    className="flex flex-col items-start gap-1"
                  >
                    <Text
                      as="div"
                      variant="body-sm"
                      className="font-semibold text-neutralBlack"
                    >
                      {b.name}
                    </Text>
                    <div className="flex items-center gap-2">
                      <Stars rating={b.rating} />
                      <Text
                        variant="body-sm"
                        className="text-darkSlate04"
                      >
                        {b.rating.toFixed(1)}
                      </Text>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          {/* RIGHT column — testimonial card */}
          <div className="flex h-full">
            <article className="flex h-full w-full flex-col justify-center rounded-3xl border border-neutralBlack/10 bg-white p-6 shadow-[0_12px_40px_rgba(20,20,40,0.06)] sm:p-7 md:p-8">
              <Text
                as="h3"
                variant="title-md"
                className="text-[18px] !font-medium text-neutralBlack sm:text-[20px]"
              >
                {testimonial.title}
              </Text>
              <Text
                as="p"
                variant="body-md"
                className="mt-4 text-[14px] leading-relaxed text-darkSlate04 sm:text-[15px]"
              >
                {testimonial.quote}
              </Text>
              <div className="mt-6 flex items-center gap-3">
                {testimonial.avatar ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="h-10 w-10 rounded-full object-cover"
                  />
                ) : (
                  <div
                    className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F97316] text-[13px] font-bold text-white"
                    aria-hidden="true"
                  >
                    {testimonial.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <Text
                    as="div"
                    variant="body-md"
                    className="text-[14px] font-semibold text-neutralBlack"
                  >
                    {testimonial.name}
                  </Text>
                  <Text
                    as="div"
                    variant="body-sm"
                    className="text-[13px] text-darkSlate04"
                  >
                    {testimonial.role}
                  </Text>
                </div>
              </div>
            </article>
          </div>
        </div>

        {/* Stats row */}
        {stats && stats.length ? (
          <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-3 md:mt-16 md:gap-6">
            {stats.map((s) => (
              <div
                key={s.label}
                className="rounded-2xl border border-neutralBlack/5 bg-white p-5 shadow-[0_8px_24px_rgba(20,20,40,0.05)] md:p-6"
              >
                <Text
                  as="div"
                  variant="title-xl"
                  className={`text-[22px] sm:text-[26px] md:text-[28px] ${s.accentColor ?? "text-neutralBlack"}`}
                >
                  {s.value}
                </Text>
                <Text
                  as="div"
                  variant="body-sm"
                  className="mt-2 text-[13px] text-darkSlate04"
                >
                  {s.label}
                </Text>
              </div>
            ))}
          </div>
        ) : null}
      </div>
      </div>
    </section>
  );
};

export default AlternativeHeroSection;
